import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import { discoveryAPI, swipeAPI } from "../services/api";
import MainLayout from "../components/MainLayout";
import DiscoveryLoading from "../components/DiscoveryLoading";
import UserProfileModal from "../components/UserProfileModal";
import AuthBackground from "../components/AuthBackground";
import { useAuth } from "../context/AuthContext";
import { cacheService } from "../services/cache";
import { getLocal, setLocal } from "../utils/storageHelper";
import { useCleanupOnUnload } from "../hooks/useCleanupOnUnload";

const ROMANTIC_QUOTES = [
  {
    text: "Every love story is beautiful, but yours is my favourite.",
    author: "Unknown",
  },
  {
    text: "The best thing to hold onto in life is each other.",
    author: "Audrey Hepburn",
  },
  { text: "You don't find love, it finds you.", author: "Anaïs Nin" },
  {
    text: "To love and be loved is to feel the sun from both sides.",
    author: "David Viscott",
  },
  { text: "The heart wants what it wants.", author: "Woody Allen" },
  {
    text: "Love is composed of a single soul inhabiting two bodies.",
    author: "Aristotle",
  },
  { text: "Where there is love there is life.", author: "Mahatma Gandhi" },
];

const HEARTS = ["💕", "❤️", "💖", "💗", "💝", "🩷", "💞"];

function Discovery() {
  const [currentProfile, setCurrentProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [swiping, setSwiping] = useState(false);
  const [showMatch, setShowMatch] = useState(false);
  const [matchId, setMatchId] = useState(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [showProfilePreview, setShowProfilePreview] = useState(false);
  const [swipeAnimation, setSwipeAnimation] = useState(null);
  const [glowIntensity, setGlowIntensity] = useState(1);

  // Empty state quote rotation
  const [quoteIndex, setQuoteIndex] = useState(0);
  const [quoteFading, setQuoteFading] = useState(false);

  const { chatList, chatListLoaded } = useAuth();
  const navigate = useNavigate();

  const loadInFlightRef = useRef(false);
  const cardRef = useRef(null);
  const skipTimeoutRef = useRef(null);
  const likeTimeoutRef = useRef(null);

  // Clear localStorage when tab is closed
  useCleanupOnUnload();

  // Pulsing glow effect - reduced intensity
  useEffect(() => {
    const interval = setInterval(() => {
      setGlowIntensity((prev) => (prev === 1 ? 1.1 : 1));
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  const loadNextProfile = useCallback(async () => {
    if (loadInFlightRef.current) return;
    loadInFlightRef.current = true;
    setLoading(true);
    setCurrentPhotoIndex(0);

    // Reset card position for next profile
    if (cardRef.current) {
      cardRef.current.style.transition = "none";
      cardRef.current.style.transform = "rotate(0deg) translateX(0px) scale(1)";
      // Force reflow
      cardRef.current.getBoundingClientRect();
      cardRef.current.style.transition =
        "transform 0.25s ease-out, box-shadow 0.25s ease-out";
    }

    try {
      // 1. Check localStorage FIRST for instant load
      const cachedProfile = getLocal("discovery_profile");
      if (cachedProfile?.data) {
        console.log("[Discovery] Loaded from localStorage");
        setCurrentProfile(cachedProfile.data);
        setLoading(false);
        loadInFlightRef.current = false;
        return;
      }

      // 2. Check sessionStorage cache
      const cachedInSession = cacheService.get("currentDiscoveryProfile");
      if (cachedInSession) {
        setCurrentProfile(cachedInSession);
        setLoading(false);
        loadInFlightRef.current = false;
        return;
      }

      // 3. Fetch from API if not cached
      const response = await discoveryAPI.getNextProfile();
      const profile = response.data.data;
      setCurrentProfile(profile);

      // Save to both localStorage and sessionStorage
      setLocal("discovery_profile", profile);
      cacheService.set("currentDiscoveryProfile", profile, {
        ttl: 1 * 60 * 1000,
        useSessionStorage: false,
      });
      console.log("[Discovery] Loaded from API, saved to localStorage");
    } catch (error) {
      console.error("Error loading profile:", error);
    } finally {
      setLoading(false);
      loadInFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    loadNextProfile();
  }, [loadNextProfile]);

  // Quote rotation — only active during empty state
  useEffect(() => {
    if (loading || currentProfile) return;

    const interval = setInterval(() => {
      setQuoteFading(true);
      setTimeout(() => {
        setQuoteIndex((i) => (i + 1) % ROMANTIC_QUOTES.length);
        setQuoteFading(false);
      }, 400);
    }, 3000);

    return () => clearInterval(interval);
  }, [loading, currentProfile]);

  // Clear animation after it plays
  useEffect(() => {
    if (swipeAnimation) {
      const timer = setTimeout(() => {
        setSwipeAnimation(null);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [swipeAnimation]);

  // Clear timeouts on unmount
  useEffect(() => {
    return () => {
      if (skipTimeoutRef.current) clearTimeout(skipTimeoutRef.current);
      if (likeTimeoutRef.current) clearTimeout(likeTimeoutRef.current);
    };
  }, []);

  const handleSkip = useCallback(
    async (e) => {
      e?.preventDefault();
      if (swiping || !currentProfile) return;

      // Clear any existing timeout
      if (skipTimeoutRef.current) clearTimeout(skipTimeoutRef.current);

      // Store profile being skipped BEFORE any state changes
      const skippedUserId = currentProfile.userId;

      // Start animation
      setSwipeAnimation("left");
      setSwiping(true);
      setShowProfilePreview(false);

      // Animate card swipe left - 1 second animation
      if (cardRef.current) {
        cardRef.current.style.transition = "transform 1s ease-out";
        cardRef.current.style.transform =
          "rotate(-15deg) translateX(-400px) scale(0.7)";
      }

      // Wait 1 second for animation to complete before changing profile
      skipTimeoutRef.current = setTimeout(async () => {
        // Clear profile cache
        cacheService.remove("currentDiscoveryProfile");
        localStorage.removeItem("discovery_profile");

        // API call in background (use the stored userId)
        try {
          await swipeAPI.skip(skippedUserId);
        } catch (error) {
          console.error("Error skipping:", error);
        }

        // Load next profile
        setCurrentProfile(null);
        await loadNextProfile();

        setSwiping(false);
        skipTimeoutRef.current = null;
      }, 1000);
    },
    [swiping, currentProfile, loadNextProfile],
  );

  const matchIdByUserId = useMemo(() => {
    if (!chatListLoaded) return new Map();
    const map = new Map();
    chatList.forEach((chat) => {
      if (!chat.otherUserId || !chat.matchId) return;
      map.set(chat.otherUserId.toString(), chat.matchId);
    });
    return map;
  }, [chatList, chatListLoaded]);

  const handleLike = useCallback(
    async (e) => {
      e?.preventDefault();
      if (swiping || !currentProfile) return;

      // Clear any existing timeout
      if (likeTimeoutRef.current) clearTimeout(likeTimeoutRef.current);

      // Store profile being liked BEFORE any state changes
      const likedUserId = currentProfile.userId;

      // Check if already matched
      if (chatListLoaded) {
        const userKey = currentProfile.userId?.toString?.();
        const existingMatchId = userKey ? matchIdByUserId.get(userKey) : null;

        if (existingMatchId) {
          navigate(`/chat/${existingMatchId}`);
          return;
        }
      }

      // Start animation
      setSwipeAnimation("right");
      setSwiping(true);
      setShowProfilePreview(false);

      // Animate card swipe right - IMMEDIATE (0.3s fast swipe)
      if (cardRef.current) {
        cardRef.current.style.transition = "transform 0.3s ease-out";
        cardRef.current.style.transform =
          "rotate(15deg) translateX(400px) scale(0.7)";
      }

      // Clear cache immediately
      cacheService.remove("currentDiscoveryProfile");
      localStorage.removeItem("discovery_profile");

      try {
        const response = await swipeAPI.like(likedUserId);

        // Successfully liked
        if (response.data.success) {
          if (response.data.data.isMatch) {
            const nextMatchId = response.data.data.matchId;
            setMatchId(nextMatchId);

            if (response.data.data.alreadyMatched) {
              navigate(`/chat/${nextMatchId}`);
            } else {
              // Show match modal
              setShowMatch(true);
              setSwiping(false);
              likeTimeoutRef.current = null;
            }
          } else {
            // Not a match - load next profile immediately
            setCurrentProfile(null);
            await loadNextProfile();
            setSwiping(false);
            likeTimeoutRef.current = null;
          }
        }
      } catch (error) {
        console.error("Error liking:", error);

        // If error is "user already liked", it means backend prevented duplicate
        const errorMessage = error.response?.data?.message || "";
        if (errorMessage.includes("already liked")) {
          // User already liked this person, move to next profile
          setCurrentProfile(null);
          await loadNextProfile();
        } else {
          // Other error - still move to next profile for smooth UX
          setCurrentProfile(null);
          await loadNextProfile();
        }
        setSwiping(false);
        likeTimeoutRef.current = null;
      }
    },
    [
      swiping,
      currentProfile,
      chatListLoaded,
      matchIdByUserId,
      navigate,
      loadNextProfile,
    ],
  );

  const handleMatchClose = useCallback(() => {
    setShowMatch(false);
    setCurrentProfile(null);
    loadNextProfile();
  }, [loadNextProfile]);

  const handleMatchChat = useCallback(() => {
    navigate(`/chat/${matchId}`);
  }, [navigate, matchId]);

  const handleClosePreview = useCallback(() => {
    setShowProfilePreview(false);
  }, []);

  const handleOpenPreview = useCallback((e) => {
    e.stopPropagation();
    setShowProfilePreview(true);
  }, []);

  const safePhotos = useMemo(
    () =>
      currentProfile?.photos?.length
        ? currentProfile.photos
        : ["https://placehold.co/800x1000?text=Add+Photo"],
    [currentProfile],
  );

  // Dynamic glow color based on swipe animation
  const getGlowColor = () => {
    if (swipeAnimation === "left") return "rgba(239, 68, 68, 0.25)";
    if (swipeAnimation === "right") return "rgba(236, 72, 153, 0.25)";
    return "rgba(219, 39, 119, 0.15)";
  };

  // Get card transform based on animation - this is for the glow effect, actual swipe is handled inline
  const getCardTransform = () => {
    if (swipeAnimation === "left") return "rotate(-3deg) scale(1.01)";
    if (swipeAnimation === "right") return "rotate(3deg) scale(1.01)";
    return "rotate(0deg) scale(1)";
  };

  return (
    <AuthBackground>
      <MainLayout className="bg-transparent" showFooter={false}>
        <DiscoveryLoading isVisible={loading} />

        <div className="relative flex flex-col items-center justify-center px-4 pt-[5px] pb-[5px] h-[calc(100vh-4rem)] overflow-hidden z-10">
          {/* ── EMPTY STATE ── */}
          {!loading && !currentProfile && (
            <div className="relative flex flex-col items-center text-center px-6 gap-6">
              {/* Floating hearts */}
              <div
                className="absolute inset-0 pointer-events-none select-none"
                aria-hidden="true"
              >
                {HEARTS.map((heart, i) => (
                  <span
                    key={i}
                    className="absolute text-xl animate-bounce opacity-10"
                    style={{
                      left: `${8 + i * 12}%`,
                      top: `${15 + (i % 3) * 28}%`,
                      animationDelay: `${i * 0.35}s`,
                      animationDuration: `${1.8 + (i % 3) * 0.6}s`,
                    }}
                  >
                    {heart}
                  </span>
                ))}
              </div>

              {/* Pulsing icon */}
              <div
                className="text-7xl select-none"
                style={{
                  animation: "pulse 2s cubic-bezier(0.4,0,0.6,1) infinite",
                }}
              >
                💘
              </div>

              {/* Heading */}
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-700 mb-1">
                  You've seen everyone nearby!
                </h2>
                <p className="text-sm sm:text-base text-gray-400">
                  New people join every day — check back soon.
                </p>
              </div>

              {/* Rotating romantic quote */}
              <div
                className="max-w-xs mx-auto bg-white/70 backdrop-blur-sm border border-pink-100 rounded-2xl px-5 py-4 shadow-sm"
                style={{
                  opacity: quoteFading ? 0 : 1,
                  transition: "opacity 0.4s ease",
                }}
              >
                <p className="text-sm text-gray-600 italic leading-relaxed">
                  &ldquo;{ROMANTIC_QUOTES[quoteIndex].text}&rdquo;
                </p>
                <p className="text-xs text-pink-400 mt-2 font-medium">
                  — {ROMANTIC_QUOTES[quoteIndex].author}
                </p>
              </div>

              {/* Try Again button */}
              <button
                onClick={loadNextProfile}
                className="mt-1 px-7 py-3 rounded-2xl bg-gradient-to-r from-pink-500 to-purple-600 text-white font-semibold text-sm shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-all duration-200"
              >
                Try Again ✨
              </button>
            </div>
          )}

          {!loading && currentProfile && (
            <>
              {/* Swipeable Card UI */}
              <div className="relative w-[90vw] max-w-[380px] sm:max-w-[400px] md:max-w-[420px] mx-auto h-[68vh] max-h-[580px] min-h-[400px] flex items-center justify-center mt-[-15px] sm:mt-[-20px] md:mt-[-30px] lg:mt-[-40px]">
                {/* LEFT SWIPE ANIMATION - Heart Breaking (PC View Only) */}
                <div className="hidden lg:block absolute left-[-180px] top-1/2 -translate-y-1/2 z-20 pointer-events-none">
                  <div
                    className={`relative flex items-center justify-center transition-all duration-500 ${
                      swipeAnimation === "left"
                        ? "opacity-100 scale-150 translate-x-[-30px]"
                        : "opacity-20 scale-100 translate-x-0"
                    }`}
                  >
                    <div className="absolute inset-0 bg-red-500/20 blur-3xl rounded-full w-40 h-40 animate-pulse"></div>

                    {/* Heart Breaking Animation */}
                    <div className="relative w-28 h-28">
                      {/* Whole Heart */}
                      <div
                        className={`absolute inset-0 flex items-center justify-center transition-all duration-300 ${
                          swipeAnimation === "left"
                            ? "opacity-0 scale-0"
                            : "opacity-100 scale-100"
                        }`}
                      >
                        <span className="text-7xl">❤️</span>
                      </div>

                      {/* Broken Heart Pieces */}
                      {swipeAnimation === "left" && (
                        <>
                          {/* Left Piece */}
                          <span className="absolute left-0 top-1/2 -translate-y-1/2 text-6xl animate-heart-break-left">
                            💔
                          </span>
                          {/* Right Piece */}
                          <span className="absolute right-0 top-1/2 -translate-y-1/2 text-6xl animate-heart-break-right">
                            💔
                          </span>
                          {/* Cracking Effect */}
                          <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-7xl animate-crack">
                            💔
                          </span>
                          {/* Shatter Particles */}
                          {[...Array(12)].map((_, i) => (
                            <span
                              key={i}
                              className="absolute text-2xl animate-shatter"
                              style={{
                                left: `${30 + Math.random() * 40}%`,
                                top: `${30 + Math.random() * 40}%`,
                                animationDelay: `${i * 0.05}s`,
                                animationDuration: "0.8s",
                                transform: `rotate(${Math.random() * 360}deg)`,
                              }}
                            >
                              ❤️‍🩹
                            </span>
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* RIGHT SWIPE ANIMATION - Two Hearts Meeting (PC View Only) */}
                <div className="hidden lg:block absolute right-[-180px] top-1/2 -translate-y-1/2 z-20 pointer-events-none">
                  <div
                    className={`relative flex items-center justify-center transition-all duration-500 ${
                      swipeAnimation === "right"
                        ? "opacity-100 scale-150 translate-x-[30px]"
                        : "opacity-20 scale-100 translate-x-0"
                    }`}
                  >
                    <div className="absolute inset-0 bg-pink-500/20 blur-3xl rounded-full w-40 h-40 animate-pulse"></div>

                    {/* Two Hearts Meeting Animation */}
                    <div className="relative w-32 h-32">
                      {/* Left Heart */}
                      <span
                        className={`absolute left-0 top-1/2 -translate-y-1/2 text-5xl transition-all duration-500 ${
                          swipeAnimation === "right"
                            ? "translate-x-[20px] opacity-0"
                            : "translate-x-0 opacity-100"
                        }`}
                      >
                        ❤️
                      </span>

                      {/* Right Heart */}
                      <span
                        className={`absolute right-0 top-1/2 -translate-y-1/2 text-5xl transition-all duration-500 ${
                          swipeAnimation === "right"
                            ? "-translate-x-[20px] opacity-0"
                            : "translate-x-0 opacity-100"
                        }`}
                      >
                        ❤️
                      </span>

                      {/* Meeting Point - Combined Heart */}
                      {swipeAnimation === "right" && (
                        <>
                          <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-7xl animate-heart-beat">
                            💕
                          </span>
                          {/* Sparkle Effect */}
                          {[...Array(8)].map((_, i) => (
                            <span
                              key={i}
                              className="absolute text-2xl animate-sparkle"
                              style={{
                                left: `${40 + Math.random() * 30}%`,
                                top: `${30 + Math.random() * 40}%`,
                                animationDelay: `${i * 0.1}s`,
                              }}
                            >
                              ✨
                            </span>
                          ))}
                          {/* Floating Small Hearts */}
                          {[...Array(6)].map((_, i) => (
                            <span
                              key={i}
                              className="absolute text-2xl animate-float-up"
                              style={{
                                left: `${20 + i * 15}%`,
                                top: `-20%`,
                                animationDelay: `${i * 0.15}s`,
                              }}
                            >
                              {["❤️", "💖", "💕", "💗"][i % 4]}
                            </span>
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* BACKGROUND GRADIENT */}
                <div className="absolute inset-0 bg-gradient-to-br from-pink-400 via-purple-400 to-indigo-400 rounded-[28px] sm:rounded-[35px] md:rounded-[40px] -rotate-6 scale-95 opacity-15 blur-sm animate-gradient" />
                <div className="absolute inset-0 bg-gradient-to-tl from-pink-300 via-purple-300 to-indigo-300 rounded-[28px] sm:rounded-[35px] md:rounded-[40px] -rotate-3 scale-95 opacity-10 blur-md" />

                {/* ACTIVE CARD with GLOWING EFFECT */}
                <div
                  ref={cardRef}
                  className="absolute inset-0 bg-white rounded-[25px] sm:rounded-[30px] md:rounded-[35px] overflow-hidden shadow-xl transition-all duration-300 ease-out"
                  style={{
                    zIndex: 2,
                    transform: getCardTransform(),
                    boxShadow: `
                    0 15px 40px -10px ${getGlowColor()},
                    0 0 0 1px rgba(255, 255, 255, 0.05),
                    0 0 15px ${glowIntensity === 1.1 ? getGlowColor() : "rgba(219, 39, 119, 0.1)"}
                  `,
                    transition:
                      "transform 0.25s ease-out, box-shadow 0.25s ease-out",
                  }}
                >
                  <div
                    className="w-full h-full bg-cover bg-center relative cursor-pointer"
                    style={{
                      backgroundImage: `linear-gradient(135deg, rgba(0,0,0,.3), rgba(0,0,0,.5)), url(${safePhotos[currentPhotoIndex]})`,
                    }}
                    onClick={handleOpenPreview}
                  >
                    {/* Gradient Overlay for better text readability */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/10" />

                    {/* Text */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 sm:px-6 translate-y-[20%]">
                      <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white drop-shadow-lg leading-tight">
                        {currentProfile.name}, {currentProfile.age}
                      </h2>
                      <p className="text-white/95 max-w-xs mt-1 mb-2 drop-shadow-md text-xs sm:text-sm md:text-base line-clamp-3">
                        {currentProfile.bio}
                      </p>
                      {currentProfile.interests && (
                        <div className="flex flex-wrap gap-2 justify-center max-w-xs mt-1">
                          {currentProfile.interests
                            .slice(0, 3)
                            .map((interest, idx) => (
                              <span
                                key={idx}
                                className="px-2 py-0.5 bg-white/20 backdrop-blur-sm rounded-full text-white text-xs border border-white/30"
                              >
                                {interest}
                              </span>
                            ))}
                        </div>
                      )}
                    </div>

                    <div className="absolute bottom-0 inset-x-0 h-24 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
                  </div>
                </div>

                {/* ACTION BUTTONS - FIXED with preventDefault */}
                <div className="absolute -bottom-8 sm:-bottom-10 md:-bottom-12 left-1/2 -translate-x-1/2 flex gap-5 sm:gap-6 md:gap-8 z-10">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      handleSkip(e);
                    }}
                    disabled={swiping}
                    type="button"
                    className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-full bg-white border-3 sm:border-4 border-[#ff6b6b] shadow-lg hover:scale-110 active:scale-95 transition-all duration-300 hover:shadow-[0_0_20px_rgba(255,107,107,0.4)]"
                  >
                    <span className="text-2xl sm:text-3xl md:text-4xl text-[#ff6b6b]">
                      ✕
                    </span>
                  </button>

                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      handleLike(e);
                    }}
                    disabled={swiping}
                    type="button"
                    className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 text-white shadow-lg hover:scale-110 active:scale-95 transition-all duration-300 hover:shadow-[0_0_20px_rgba(236,72,153,0.4)]"
                  >
                    <span className="text-2xl sm:text-3xl md:text-4xl">♥</span>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Profile Preview Modal */}
        <UserProfileModal
          userId={currentProfile?.userId}
          isOpen={showProfilePreview}
          onClose={handleClosePreview}
          onLike={handleLike}
          onSkip={handleSkip}
          disableActions={swiping}
          prefetchedProfile={currentProfile}
        />

        {showMatch && (
          <div
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
            onClick={handleMatchClose}
          >
            <div
              className="bg-white rounded-2xl p-10 max-w-[500px] w-11/12 text-center"
              onClick={(e) => e.stopPropagation()}
            >
              <h1 className="text-4xl text-gray-800 mb-4">It's a Match! 🎉</h1>
              <p className="text-lg text-gray-500 mb-6">
                You and {currentProfile?.name} liked each other!
              </p>
              <div className="flex gap-4 justify-center">
                <button
                  className="px-5 py-2.5 rounded-xl bg-gray-100 text-gray-800 font-semibold hover:bg-gray-200 transition"
                  onClick={handleMatchClose}
                  type="button"
                >
                  Keep Swiping
                </button>
                <button
                  className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold shadow-md hover:shadow-lg transition"
                  onClick={handleMatchChat}
                  type="button"
                >
                  Send Message
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Custom animations */}
        <style jsx>{`
          /* Hide corner waves at 1031px and below */
          @media (max-width: 1031px) {
            .corner-wave-top,
            .corner-wave-bottom {
              display: none !important;
            }
          }

          @keyframes heart-break-left {
            0% {
              transform: translateX(0) rotate(0deg);
              opacity: 1;
            }
            50% {
              transform: translateX(-30px) rotate(-20deg);
              opacity: 0.8;
            }
            100% {
              transform: translateX(-60px) rotate(-45deg);
              opacity: 0;
            }
          }
          .animate-heart-break-left {
            animation: heart-break-left 0.6s ease-out forwards;
          }

          @keyframes heart-break-right {
            0% {
              transform: translateX(0) rotate(0deg);
              opacity: 1;
            }
            50% {
              transform: translateX(30px) rotate(20deg);
              opacity: 0.8;
            }
            100% {
              transform: translateX(60px) rotate(45deg);
              opacity: 0;
            }
          }
          .animate-heart-break-right {
            animation: heart-break-right 0.6s ease-out forwards;
          }

          @keyframes crack {
            0% {
              transform: translate(-50%, -50%) scale(1);
              opacity: 1;
            }
            50% {
              transform: translate(-50%, -50%) scale(1.5);
              opacity: 0.8;
            }
            100% {
              transform: translate(-50%, -50%) scale(0);
              opacity: 0;
            }
          }
          .animate-crack {
            animation: crack 0.5s ease-out forwards;
          }

          @keyframes shatter {
            0% {
              transform: translate(0, 0) scale(1);
              opacity: 1;
            }
            100% {
              transform: translate(
                  ${() => (Math.random() - 0.5) * 100}px,
                  ${() => (Math.random() - 0.5) * 100}px
                )
                scale(0);
              opacity: 0;
            }
          }
          .animate-shatter {
            animation: shatter 0.8s ease-out forwards;
          }

          @keyframes heart-beat {
            0% {
              transform: translate(-50%, -50%) scale(0);
              opacity: 0;
            }
            50% {
              transform: translate(-50%, -50%) scale(1.5);
              opacity: 1;
            }
            100% {
              transform: translate(-50%, -50%) scale(1);
              opacity: 1;
            }
          }
          .animate-heart-beat {
            animation: heart-beat 0.5s ease-out forwards;
          }

          @keyframes sparkle {
            0% {
              transform: scale(0) rotate(0deg);
              opacity: 0;
            }
            50% {
              transform: scale(1.5) rotate(180deg);
              opacity: 1;
            }
            100% {
              transform: scale(0) rotate(360deg);
              opacity: 0;
            }
          }
          .animate-sparkle {
            animation: sparkle 0.8s ease-out forwards;
          }

          @keyframes float-up {
            0% {
              transform: translateY(0) scale(1);
              opacity: 1;
            }
            100% {
              transform: translateY(-70px) scale(1.5);
              opacity: 0;
            }
          }
          .animate-float-up {
            animation: float-up 1.1s ease-out forwards;
          }

          @keyframes blob {
            0% {
              transform: translate(0px, 0px) scale(1);
            }
            33% {
              transform: translate(20px, -30px) scale(1.05);
            }
            66% {
              transform: translate(-15px, 15px) scale(0.95);
            }
            100% {
              transform: translate(0px, 0px) scale(1);
            }
          }
          .animate-blob {
            animation: blob 8s infinite;
          }
          .animation-delay-2000 {
            animation-delay: 2s;
          }
          .animation-delay-4000 {
            animation-delay: 4s;
          }

          @keyframes gradient {
            0% {
              opacity: 0.15;
            }
            50% {
              opacity: 0.2;
            }
            100% {
              opacity: 0.15;
            }
          }
          .animate-gradient {
            animation: gradient 5s ease-in-out infinite;
          }
        `}</style>
      </MainLayout>
    </AuthBackground>
  );
}

export default Discovery;
